import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import UserIcon from '~/images/apps/user-icon.svg';
import { AppBar, Popover, Toolbar, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import BaseBreadcrumbs from '@libs/@core/components/breadcrumbs/BaseBreadcrumbs';
import { LangIcon } from '@import/icons';
import { clearAccessTokenCookie } from '@libs/utils/serverHelpers';
import useGetProfileData from '@libs/hooks/useGetProfileData';
import { listLangs } from '@libs/hooks/useGetLanguageValues';
import useCustomTranslation from '@libs/hooks/useCustomTranslation';
var langBreadcrumb = {
    'cf-management': 'L000081',
    'cf-template': 'L000080',
    'cf-statement': 'L000079',
    'account-management': 'L000002',
    'dept-management': 'L000054',
    cashflow: 'L000017',
    dashboard: 'L000053',
    'aggregrate-cf': 'L000006',
    'multi-languages': 'L000019',
    'cf-overview': 'L000048',
    edit: 'L000023',
    'ap-management': 'L000136',
    'ar-management': 'L000184',
    'ap-overview': 'L000147',
    'ar-overview': 'L000169',
    'supplier-management': 'L000124',
    'customer-management': 'L000141',
    'supplier-balance': 'L000137',
    'customer-balance': 'L000142',
    'line-items': 'L000204',
    automation: 'L000230',
    payment: 'L000231',
    create: 'L000232',
    'payment-log': 'L000208',
    'execution-log': 'L000228',
    'dunning-letter': 'L000234'
};
var PopoverUser = function () {
    var t = useCustomTranslation();
    var queryClient = useQueryClient();
    var _a = React.useState(null), anchorEl = _a[0], setAnchorEl = _a[1];
    var open = Boolean(anchorEl);
    var id = open ? 'simple-popover' : undefined;
    var handleClick = function (event) {
        setAnchorEl(event.currentTarget);
    };
    var handleClose = function () {
        setAnchorEl(null);
    };
    var handleLogout = function () {
        queryClient.clear();
        clearAccessTokenCookie();
        setTimeout(function () {
            window.location.href = '/auth/login';
        }, 1);
    };
    return (_jsxs("div", { children: [_jsx("div", { "aria-describedby": id, onClick: handleClick, className: 'h-[20px]', children: _jsx("img", { src: UserIcon, alt: 'Smart CFO Logo', className: 'cursor-pointer' }) }), _jsx(Popover, { id: id, open: open, anchorEl: anchorEl, onClose: handleClose, anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                }, children: _jsx(Typography, { onClick: handleLogout, sx: {
                        p: 2,
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: 'gray.100'
                        }
                    }, children: t('L000082') }) })] }));
};
var PopoverLanguage = function () {
    var t = useCustomTranslation();
    var _a = React.useState(null), anchorEl = _a[0], setAnchorEl = _a[1];
    var open = Boolean(anchorEl);
    var id = open ? 'simple-popover' : undefined;
    var handleClick = function (event) {
        setAnchorEl(event.currentTarget);
    };
    var handleClose = function () {
        setAnchorEl(null);
    };
    var handleChangeLanguage = function (lang) {
        localStorage.setItem('lang', lang);
        window.location.reload();
    };
    return (_jsxs("div", { children: [_jsxs("div", { "aria-describedby": id, onClick: handleClick, className: 'flex items-center gap-2 p-2 border-solid border-gray-300 rounded-md cursor-pointer', children: [_jsx(LangIcon, {}), _jsx(Typography, { variant: 'h6', children: localStorage.getItem('lang') === 'en' ? t('L000021') : t('L000022') })] }), _jsx(Popover, { id: id, open: open, anchorEl: anchorEl, onClose: handleClose, anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                }, children: listLangs.map(function (lang) { return (_jsx(Typography, { onClick: function () { return handleChangeLanguage(lang); }, sx: {
                        p: 2,
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: 'gray.100'
                        }
                    }, children: lang === 'en' ? t('L000021') : t('L000022') }, lang)); }) })] }));
};
var Header = function () {
    // ---------------------------------
    // State, Store, Hooks and Form Initialization
    // ---------------------------------
    var t = useCustomTranslation();
    var location = useLocation();
    // ---------------------------------
    // Query & Mutation
    // ---------------------------------
    var profileData = useGetProfileData().data;
    var listBreadcrumb = useMemo(function () {
        var routes = location.pathname.split('/');
        if (!routes[routes.length - 1])
            routes.pop();
        var filteredRoutes = routes.filter(function (route) { return langBreadcrumb[route]; });
        var path = '';
        var listRouters = filteredRoutes.map(function (route, index) {
            if (index !== 0) {
                path += '/' + route;
            }
            return {
                label: t(langBreadcrumb[route] || route),
                isLink: index !== 0 && index !== 1,
                href: path
            };
        });
        return listRouters;
    }, [location, t]);
    return (_jsxs(AppBar, { children: [_jsx(Toolbar, { children: _jsxs("div", { className: 'flex items-center w-full h-full justify-end gap-3', children: [_jsx(PopoverLanguage, {}), profileData && _jsx(Typography, { variant: 'h6', children: profileData.fullName }), _jsx(PopoverUser, {})] }) }), _jsx("div", { className: 'px-3 py-2 bg-gray-100 w-full', children: _jsx(BaseBreadcrumbs, { listBreadcrumb: listBreadcrumb }) })] }));
};
export default Header;
